import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, toISODate, startOfWeek, addDays, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Repeat, CalendarDays, TrendingUp, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const JOB_COLORS = ["#0e7490", "#7c3aed", "#b45309", "#be123c", "#15803d", "#0369a1", "#a16207", "#9333ea"];

const monthGrid = (viewDate) => {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const cells = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(gridStart, i));
  return cells;
};

export default function Schedule() {
  const [jobs, setJobs] = useState([]);
  const [view, setView] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState("single");
  const [single, setSingle] = useState({ job_id: "", date: toISODate(new Date()), hours: "" });
  const [rec, setRec] = useState({ job_id: "", hours: "", start_date: toISODate(startOfWeek()), weeks: "6", weekdays: [0, 2, 4] });
  const [saving, setSaving] = useState(false);

  const cells = monthGrid(view);
  const gridFrom = toISODate(cells[0]);
  const gridTo = toISODate(cells[41]);
  const monthLabel = view.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const jobColor = useCallback((jid) => {
    const idx = jobs.findIndex((j) => j.id === jid);
    return JOB_COLORS[(idx >= 0 ? idx : 0) % JOB_COLORS.length];
  }, [jobs]);

  const loadJobs = useCallback(async () => {
    const j = (await api.get("/jobs")).data;
    setJobs(j);
    if (j.length) {
      setSingle((s) => ({ ...s, job_id: s.job_id || j[0].id }));
      setRec((r) => ({ ...r, job_id: r.job_id || j[0].id }));
    }
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      setEntries((await api.get(`/schedule?date_from=${gridFrom}&date_to=${gridTo}`)).data);
    } finally { setLoading(false); }
  }, [gridFrom, gridTo]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const byDate = {};
  entries.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });

  const monthEntries = entries.filter((e) => new Date(e.date + "T00:00:00").getMonth() === view.getMonth());
  const monthHours = monthEntries.reduce((s, e) => s + e.hours, 0);
  const monthIncome = monthEntries.reduce((s, e) => s + e.amount, 0);
  const busyDays = new Set(monthEntries.map((e) => e.date)).size;

  const openAddFor = (dateIso) => {
    setAddMode("single");
    setSingle((s) => ({ ...s, date: dateIso, job_id: s.job_id || (jobs[0] && jobs[0].id) || "" }));
    setAddOpen(true);
  };

  const addSingle = async () => {
    if (!single.job_id) return toast.error("Select a job");
    const h = parseFloat(single.hours);
    if (!h || h <= 0) return toast.error("Enter valid hours");
    setSaving(true);
    try {
      await api.post("/schedule", { job_id: single.job_id, date: single.date, hours: h });
      toast.success("Scheduled");
      setSingle((s) => ({ ...s, hours: "" }));
      setAddOpen(false); loadEntries();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add"); }
    finally { setSaving(false); }
  };

  const toggleWeekday = (wd) =>
    setRec((r) => ({ ...r, weekdays: r.weekdays.includes(wd) ? r.weekdays.filter((x) => x !== wd) : [...r.weekdays, wd] }));

  const addRecurring = async () => {
    if (!rec.job_id) return toast.error("Select a job");
    const h = parseFloat(rec.hours);
    const w = parseInt(rec.weeks, 10);
    if (!h || h <= 0) return toast.error("Enter valid hours per day");
    if (!rec.weekdays.length) return toast.error("Pick at least one day of the week");
    if (!w || w < 1) return toast.error("Enter number of weeks");
    setSaving(true);
    try {
      const res = await api.post("/schedule/recurring", { job_id: rec.job_id, hours: h, weekdays: rec.weekdays, start_date: rec.start_date, weeks: w });
      toast.success(`${res.data.created} sessions scheduled`);
      setRec((r) => ({ ...r, hours: "" }));
      setAddOpen(false); loadEntries();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add"); }
    finally { setSaving(false); }
  };

  const del = async (entry, scope) => {
    try { await api.delete(`/schedule/${entry.id}?scope=${scope}`); toast.success("Removed"); loadEntries(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  const todayIso = toISODate(new Date());

  return (
    <div>
      <PageHeader title="Schedule" subtitle="See all your scheduled jobs on a calendar and spot free time to take on more work."
        actions={<div className="flex gap-2">
          <Button variant="outline" data-testid="schedule-open-forecast" onClick={() => navigate("/forecast")}><TrendingUp className="h-4 w-4 mr-2" />Forecast</Button>
          <Button data-testid="schedule-add-button" onClick={() => { setAddMode("single"); setAddOpen(true); }}><Plus className="h-4 w-4 mr-2" />Schedule work</Button>
        </div>} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <Card className="p-4"><div className="text-xs text-muted-foreground">Scheduled hours ({view.toLocaleDateString("en-US", { month: "short" })})</div><div className="text-2xl font-semibold tabular-nums font-display mt-1" data-testid="schedule-month-hours">{monthHours.toFixed(1)}</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Expected income</div><div className="text-2xl font-semibold tabular-nums font-display mt-1 text-[color:var(--kpi-positive)]" data-testid="schedule-month-income">{money(monthIncome)}</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Busy days this month</div><div className="text-2xl font-semibold tabular-nums font-display mt-1" data-testid="schedule-busy-days">{busyDays}</div></Card>
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold font-display" data-testid="schedule-month-label">{monthLabel}</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" data-testid="schedule-prev-month" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" data-testid="schedule-today" onClick={() => setView(new Date())}>Today</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" data-testid="schedule-next-month" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b bg-secondary/40">
          {WD.map((d) => <div key={d} className="px-2 py-2 text-center text-xs font-medium text-muted-foreground">{d}</div>)}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((cell, i) => {
            const iso = toISODate(cell);
            const inMonth = cell.getMonth() === view.getMonth();
            const dayEntries = byDate[iso] || [];
            const isToday = iso === todayIso;
            return (
              <button
                key={i}
                type="button"
                data-testid={`schedule-day-${iso}`}
                onClick={() => openAddFor(iso)}
                className={`min-h-[92px] text-left p-1.5 border-b border-r last:border-r-0 flex flex-col gap-1 transition-colors hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring ${inMonth ? "bg-card" : "bg-secondary/30"}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs tabular-nums h-5 w-5 flex items-center justify-center rounded-full ${isToday ? "bg-primary text-primary-foreground font-semibold" : inMonth ? "text-foreground" : "text-muted-foreground/50"}`}>{cell.getDate()}</span>
                  {dayEntries.length === 0 && inMonth && <span className="text-[9px] text-muted-foreground/50">free</span>}
                </div>
                <div className="flex flex-col gap-0.5 overflow-hidden">
                  {dayEntries.slice(0, 3).map((e) => (
                    <span key={e.id} className="text-[10px] leading-tight rounded px-1 py-0.5 truncate text-white" style={{ background: jobColor(e.job_id) }} title={`${e.job_title} · ${e.hours}h`}>
                      {e.hours}h {e.job_title}
                    </span>
                  ))}
                  {dayEntries.length > 3 && <span className="text-[9px] text-muted-foreground">+{dayEntries.length - 3} more</span>}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Legend */}
      {jobs.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-4">
          {jobs.map((j) => (
            <div key={j.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="h-3 w-3 rounded-sm" style={{ background: jobColor(j.id) }} />{j.title}
            </div>
          ))}
        </div>
      )}

      {/* Upcoming list for the month */}
      <Card className="overflow-hidden mt-6">
        <div className="p-4 border-b"><h3 className="text-lg font-semibold font-display">Scheduled blocks · {view.toLocaleDateString("en-US", { month: "long" })}</h3></div>
        {monthEntries.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Nothing scheduled this month. Click any day above to add a block.</div>
        ) : (
          <div className="divide-y" data-testid="schedule-blocks-list">
            {[...monthEntries].sort((a, b) => a.date.localeCompare(b.date)).map((e) => (
              <div key={e.id} className="flex items-center justify-between p-3 hover:bg-accent/40" data-testid={`schedule-block-${e.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-semibold shrink-0" style={{ background: jobColor(e.job_id) }}><Clock className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{e.job_title} <span className="text-muted-foreground font-normal">· {e.hours}h</span></div>
                    <div className="text-xs text-muted-foreground">{fmtDate(e.date)} · {money(e.amount)} {e.recurring_group && <Badge variant="secondary" className="ml-1 gap-1 py-0"><Repeat className="h-2.5 w-2.5" />series</Badge>}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`schedule-block-delete-${e.id}`} onClick={() => del(e, "one")} title="Delete this block"><Trash2 className="h-4 w-4" /></Button>
                  {e.recurring_group && <Button variant="ghost" size="sm" className="text-destructive text-xs" data-testid={`schedule-block-delete-series-${e.id}`} onClick={() => del(e, "series")}>Series</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="schedule-add-dialog">
          <DialogHeader><DialogTitle>Schedule work</DialogTitle></DialogHeader>
          {jobs.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              You need a job first. <Button variant="link" className="px-1" onClick={() => navigate("/jobs")}>Create a job</Button>
            </div>
          ) : (
          <>
          <Tabs value={addMode} onValueChange={setAddMode} className="mt-1">
            <TabsList className="w-full">
              <TabsTrigger value="single" className="flex-1" data-testid="schedule-mode-single"><CalendarDays className="h-4 w-4 mr-1" />Single date</TabsTrigger>
              <TabsTrigger value="recurring" className="flex-1" data-testid="schedule-mode-recurring"><Repeat className="h-4 w-4 mr-1" />Recurring</TabsTrigger>
            </TabsList>
          </Tabs>

          {addMode === "single" ? (
            <div className="space-y-4 mt-2">
              <div className="space-y-1.5"><Label>Job</Label>
                <Select value={single.job_id} onValueChange={(v) => setSingle({ ...single, job_id: v })}>
                  <SelectTrigger data-testid="schedule-single-job"><SelectValue placeholder="Select job" /></SelectTrigger>
                  <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} — {money(j.hourly_rate)}/hr</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Date</Label><DatePicker value={single.date} onChange={(v) => setSingle({ ...single, date: v })} testid="schedule-single-date" /></div>
              <div className="space-y-1.5"><Label>Hours</Label><Input type="number" step="0.5" min="0" value={single.hours} onChange={(e) => setSingle({ ...single, hours: e.target.value })} data-testid="schedule-single-hours" placeholder="4" /></div>
              <DialogFooter><Button onClick={addSingle} disabled={saving} data-testid="schedule-single-save"><Plus className="h-4 w-4 mr-2" />Add to schedule</Button></DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              <div className="space-y-1.5"><Label>Job</Label>
                <Select value={rec.job_id} onValueChange={(v) => setRec({ ...rec, job_id: v })}>
                  <SelectTrigger data-testid="schedule-rec-job"><SelectValue placeholder="Select job" /></SelectTrigger>
                  <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} — {money(j.hourly_rate)}/hr</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Repeat on</Label>
                <div className="flex gap-1" data-testid="schedule-rec-weekdays">
                  {WD.map((d, idx) => (
                    <button key={d} type="button" data-testid={`schedule-weekday-${idx}`} onClick={() => toggleWeekday(idx)}
                      className={`flex-1 h-9 rounded-md text-xs font-medium border transition-colors ${rec.weekdays.includes(idx) ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground hover:bg-accent"}`}>
                      {d.charAt(0)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5"><Label>Hours per day</Label><Input type="number" step="0.5" min="0" value={rec.hours} onChange={(e) => setRec({ ...rec, hours: e.target.value })} data-testid="schedule-rec-hours" placeholder="4" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Starting</Label><DatePicker value={rec.start_date} onChange={(v) => setRec({ ...rec, start_date: v })} testid="schedule-rec-start" /></div>
                <div className="space-y-1.5"><Label># of weeks</Label><Input type="number" step="1" min="1" value={rec.weeks} onChange={(e) => setRec({ ...rec, weeks: e.target.value })} data-testid="schedule-rec-weeks" placeholder="6" /></div>
              </div>
              <DialogFooter><Button onClick={addRecurring} disabled={saving} data-testid="schedule-rec-save"><Repeat className="h-4 w-4 mr-2" />Schedule sessions</Button></DialogFooter>
            </div>
          )}
          </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
